/**
 * Geometry for the visible "AI generated" label we bake into images for
 * platforms with no AI-disclosure API (LinkedIn — see `aiDisclosure.ts`).
 *
 * Pure and dependency-free so the backend renderer (`bakeAiLabel.ts`) and the
 * frontend preview overlay import the SAME numbers. If the preview and the
 * posted image disagree, users stop trusting the preview — so every dimension a
 * renderer needs comes from here, and nothing is hardcoded at a call site.
 */

/** Label copy. Short enough not to crowd the image, explicit enough to read as
 *  a disclosure rather than a watermark. */
export const AI_LABEL_TEXT = 'AI generated'

export type AiLabelGeometry = {
  fontSize: number
  /** Exact advance width the text is forced to occupy, via SVG `textLength`.
   *  Pinning it removes any dependence on which font actually resolves, so the
   *  pill can never clip its own label. */
  textWidth: number
  /** Pill padding either side of the text. */
  paddingX: number
  /** Pill padding above and below the text. */
  paddingY: number
  /** Overall pill width. */
  width: number
  /** Overall pill height. */
  height: number
  /** Corner radius — half the height, so the pill is fully rounded. */
  radius: number
  /** Gap between the pill and the image edge. */
  margin: number
  /** Pill background opacity over the image, 0–1. */
  backgroundAlpha: number
}

/**
 * Label font size as a fraction of the image's shorter side. Tuned to sit close
 * to how the platforms render their own disclosures: clearly readable at feed
 * scale and after platform re-compression, without dominating the artwork. On a
 * 1080px square this lands at ~30px text in a ~230px pill.
 */
const TARGET_RATIO = 0.028
/** Floor so a small image still gets a legible label. */
const MIN_FONT_SIZE = 12
/** Ceiling so a huge image doesn't get a billboard. */
const MAX_FONT_SIZE = 40
/**
 * Average glyph advance as a fraction of font size, for the mixed-case Latin
 * text we use. Only needs to be close — `textLength` pins the real width to
 * whatever this produces, so the result is exact rather than approximate.
 */
const ADVANCE_RATIO = 0.53

/**
 * Resolves label geometry for an image of the given pixel dimensions. Scales
 * with the shorter side so the badge reads the same on a square post and a tall
 * story.
 */
export function aiLabelGeometry(
  imageWidth: number,
  imageHeight: number,
  text: string = AI_LABEL_TEXT,
  /** User scale multiplier. Applied before the clamps, so the floor and ceiling
   *  still bound the result and a scale of 0.01 cannot produce a 0px label. */
  scale: number = 1,
): AiLabelGeometry {
  const shortSide = Math.min(imageWidth, imageHeight)
  const fontSize = Math.round(
    Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, shortSide * TARGET_RATIO * scale)),
  )

  const textWidth = Math.round(fontSize * ADVANCE_RATIO * text.length)
  const paddingX = Math.round(fontSize * 0.6)
  const paddingY = Math.round(fontSize * 0.34)
  const height = fontSize + paddingY * 2

  return {
    fontSize,
    textWidth,
    paddingX,
    paddingY,
    width: textWidth + paddingX * 2,
    height,
    radius: Math.round(height / 2),
    margin: Math.round(fontSize * 0.7),
    backgroundAlpha: 0.62,
  }
}

/**
 * Top-left pixel offset for the label within the image, for a chosen corner.
 *
 * Shared so the baker's composite offset and the preview's CSS offsets come from
 * one calculation. They drifted apart once already: the label was hardcoded
 * bottom-left in the baker while the preview positioned from its own arithmetic.
 */
export function aiLabelOffset(
  imageWidth: number,
  imageHeight: number,
  geometry: Pick<AiLabelGeometry, 'width' | 'height' | 'margin'>,
  position: 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight',
): { x: number; y: number } {
  const { width, height, margin } = geometry
  const right = Math.max(0, imageWidth - width - margin)
  const bottom = Math.max(0, imageHeight - height - margin)

  switch (position) {
    case 'topLeft':
      return { x: margin, y: margin }
    case 'topRight':
      return { x: right, y: margin }
    case 'bottomRight':
      return { x: right, y: bottom }
    case 'bottomLeft':
      return { x: margin, y: bottom }
  }
}
